import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Checkbox } from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import classes from "./CohortsList.scss";
import ProfileCascade from "UIComponents/ProfileCascade";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";

const avatarStyles = {
  size: "medium",
  shade: "light",
  randomizeColor: true,
  shape: "square",
};

const CohortSelectionCard = props => {
  const {
    userList = [],
    title,
    id,
    cohortType,
    onAddCohort,
    onRemoveCohort,
    isChecked,
    isIndeterminate,
    type,
    isRevampEnabled,
    testId,
  } = props;

  const { t } = useI18n(["common"]);
  const componentTestId = testId ? testId : undefined;
  const userCount = _.size(userList);

  const mapSubtitleTextToUserType = {
    [STUDENT]: t("common:student_count", { count: userCount }),
    [STAFF]: t("common:staff_count_lowercase", { count: userCount }),
    [PARENT]: t("common:family_member_count", { count: userCount }),
  };

  const userSubTitleText = mapSubtitleTextToUserType[type];

  const handleCohortClick = () => {
    const cohortId = `${id}-${type}`;
    if (isChecked || isIndeterminate) {
      onRemoveCohort({ cohortType, cohortId });
    } else {
      onAddCohort({ users: userList, cohortType, cohortId });
    }
  };

  const cohortTitleClasses = classNames(classes.userTitle, {
    "text-body-1": !isRevampEnabled,
    "text-body": isRevampEnabled,
  });
  const cohortSubTitleClasses = classNames({
    [classes.userSubTitle]: !isRevampEnabled,
    [classes.userSubTitleRevamp]: isRevampEnabled,
    "text-body-2": !isRevampEnabled,
    "text-body-s": isRevampEnabled,
  });
  return (
    <div className={classes.selectionCardContainer}>
      <div className={classes.userAvatar}>
        <ProfileCascade
          items={[
            {
              id,
              value: title,
            },
          ]}
          shouldDynamicColor
          showAvatar
          avatarStyles={avatarStyles}
        />
      </div>
      <div className={classes.userInfoContainer}>
        <div className={cohortTitleClasses}>{title}</div>
        <div className={cohortSubTitleClasses}>{userSubTitleText}</div>
      </div>
      <div className={classes.checkboxContainer}>
        <Checkbox
          onChange={handleCohortClick}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          testId={componentTestId}
        />
      </div>
    </div>
  );
};

const CohortsList = props => {
  const {
    headerTitle,
    onAddCohort = _.noop,
    onRemoveCohort = _.noop,
    cohorts,
    selectedState,
    type: cohortType,
    testId,
    showCohortHeader = true,
    isRevampEnabled = false,
  } = props;

  const componentTestId = testId ? `${testId}-cohortsList` : undefined;
  const headerContainer = classNames(classes.headerContainer, "text-label-2");

  if (_.isEmpty(cohorts)) {
    return null;
  }

  return (
    <div className={classes.container}>
      {showCohortHeader ? (
        <div className={headerContainer}>{headerTitle}</div>
      ) : null}
      {_.map(cohorts, cohort => {
        const { userList, id, title, type } = cohort;
        const key = `${id}-${type}`;
        const isChecked = !!_.get(selectedState, [key], false);
        const isIndeterminate = !_.isEmpty(
          _.get(selectedState, [key, "removedUsersIds"], [])
        );

        return (
          <CohortSelectionCard
            key={key}
            id={id}
            type={type}
            title={title}
            cohortType={cohortType}
            onAddCohort={onAddCohort}
            onRemoveCohort={onRemoveCohort}
            userList={userList}
            isChecked={isChecked}
            isIndeterminate={isIndeterminate}
            isRevampEnabled={isRevampEnabled}
            testId={`${componentTestId}-cohortSelectionCard-${key}`}
          />
        );
      })}
    </div>
  );
};

CohortsList.propTypes = {
  cohorts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      userList: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ),
  onAddCohort: PropTypes.func.isRequired,
  onRemoveCohort: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  selectedState: PropTypes.object,
};

export default CohortsList;
